import {
  BILL_DENOMINATIONS,
  COIN_DENOMINATIONS,
  DenominationItem,
  computeDenominationTotal,
  formatCurrency,
  formatDenominationLabel,
} from '../utils/denominations';

type DenominationGridProps = {
  value: DenominationItem[];
  onChange: (items: DenominationItem[]) => void;
  disabled?: boolean;
  showTotal?: boolean;
};

function updateQuantity(
  items: DenominationItem[],
  denomination: number,
  quantity: number
): DenominationItem[] {
  return items.map((item) =>
    item.denomination === denomination ? { ...item, quantity: Math.max(0, quantity) } : item
  );
}

function DenominationSection({
  title,
  denominations,
  items,
  onChange,
  disabled,
}: {
  title: string;
  denominations: readonly number[];
  items: DenominationItem[];
  onChange: (items: DenominationItem[]) => void;
  disabled?: boolean;
}) {
  return (
    <div>
      <div className="text-xs font-semibold uppercase tracking-wide text-stone-500">{title}</div>
      <div className="mt-2 grid gap-2 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4">
        {denominations.map((denomination) => {
          const quantity = items.find((i) => i.denomination === denomination)?.quantity ?? 0;
          return (
            <div
              key={denomination}
              className="flex items-center justify-between gap-2 rounded-lg border border-stone-200 bg-stone-50/50 px-3 py-2"
            >
              <span className="text-sm font-medium text-stone-800">{formatDenominationLabel(denomination)}</span>
              <input
                type="number"
                min={0}
                step={1}
                value={quantity || ''}
                onChange={(e) => {
                  const next = Number(e.target.value);
                  onChange(updateQuantity(items, denomination, Number.isFinite(next) ? next : 0));
                }}
                disabled={disabled}
                className="w-20 rounded-md border border-stone-200 bg-white px-2 py-1.5 text-right text-sm outline-none focus:border-emerald-500 focus:ring-2 focus:ring-emerald-500/20 disabled:opacity-60"
                placeholder="0"
              />
            </div>
          );
        })}
      </div>
    </div>
  );
}

export default function DenominationGrid({
  value,
  onChange,
  disabled = false,
  showTotal = true,
}: DenominationGridProps) {
  const total = computeDenominationTotal(value);

  return (
    <div className="space-y-4">
      <DenominationSection
        title="Bills"
        denominations={BILL_DENOMINATIONS}
        items={value}
        onChange={onChange}
        disabled={disabled}
      />
      <DenominationSection
        title="Coins"
        denominations={COIN_DENOMINATIONS}
        items={value}
        onChange={onChange}
        disabled={disabled}
      />
      {showTotal ? (
        <div className="flex items-center justify-between rounded-lg border border-emerald-200 bg-emerald-50 px-4 py-3">
          <span className="text-sm font-medium text-emerald-900">Total counted</span>
          <span className="text-lg font-bold text-emerald-800">{formatCurrency(total)}</span>
        </div>
      ) : null}
    </div>
  );
}
